import TINY_PLAN from './tiny'
import SOLO_PLAN from './solo'
import PRO_PLAN from './pro'
import API_PLAN from './api'
import MAX_PLAN from './max'
import { normalizeCurrencyCode } from '../currency'
import { getTeamMemberPriceIdBySymbol, getAdditionalTeamMemberPrice, DEFAULT_TEAM_LIMIT } from './teamMember'

export { getTeamMemberPriceIdBySymbol, getAdditionalTeamMemberPrice, DEFAULT_TEAM_LIMIT }

export const PERIOD_MONTH = 'monthly'
export const PERIOD_YEAR = 'yearly'
const convertToBytes = (size) => 1024 * 1024 * size

export const FREE_PLAN = {
  prices: {
    yearly: { value: 0 },
    monthly: { value: 0 }
  },
  label: 'Free',
  projectLimit: 1,
  fileSizeLimit: 3,
  visitorLimit: 5000,
  bandwidthLimit: 5,
  customDomainsLimit: 0
}

export const PRO_MAX_FILE_SIZES = {
  [TINY_PLAN.id]: TINY_PLAN.fileSizeLimit,
  [PRO_PLAN.id]: PRO_PLAN.fileSizeLimit,
  [SOLO_PLAN.id]: SOLO_PLAN.fileSizeLimit,
  [MAX_PLAN.id]: MAX_PLAN.fileSizeLimit
}

export const PRO_CUSTOM_DOMAIN_LIMIT = {
  [TINY_PLAN.id]: TINY_PLAN.customDomainsLimit,
  [SOLO_PLAN.id]: SOLO_PLAN.customDomainsLimit,
  [PRO_PLAN.id]: PRO_PLAN.customDomainsLimit,
  [MAX_PLAN.id]: MAX_PLAN.customDomainsLimit
}

export const PLANS = {
  FREE: FREE_PLAN,
  TINY: TINY_PLAN,
  PRO: SOLO_PLAN, // Solo
  PRO_U: PRO_PLAN, // Pro
  PRO_MAX: MAX_PLAN,
  API: API_PLAN
}

export const PLANS_ID = {
  [TINY_PLAN.id]: TINY_PLAN,
  [SOLO_PLAN.id]: SOLO_PLAN,
  [PRO_PLAN.id]: PRO_PLAN,
  [API_PLAN.id]: API_PLAN,
  [MAX_PLAN.id]: MAX_PLAN
}

export const PLAN_RANK = {
  [TINY_PLAN.id]: 1,
  [SOLO_PLAN.id]: 2,
  [PRO_PLAN.id]: 3,
  [MAX_PLAN.id]: 4
}

export const PLAN_ORDER = {
  [TINY_PLAN.id]: SOLO_PLAN.id,
  [SOLO_PLAN.id]: PRO_PLAN.id,
  [PRO_PLAN.id]: MAX_PLAN.id,
  [MAX_PLAN.id]: MAX_PLAN.id
}

const CURRENCY_CODE_TO_SYMBOL = { usd: '$', eur: '€', gbp: '£', inr: '₹' }

export const getPlanPrice = (planId, interval, currencyOrSymbol) => {
  const plan = PLANS_ID[planId]
  const priceBlock = plan?.prices?.[interval]
  if (!priceBlock) return null

  const code = normalizeCurrencyCode(currencyOrSymbol)
  let stripeEntry = priceBlock.stripe?.[code]
  let isFallbackCurrency = false
  if (!stripeEntry && code !== 'usd') {
    stripeEntry = priceBlock.stripe?.usd
    isFallbackCurrency = true
  }

  return {
    planId,
    interval,
    symbol: stripeEntry?.symbol || '$',
    value: stripeEntry?.value ?? priceBlock.value,
    isFallbackCurrency,
    stripe: { priceId: stripeEntry?.priceId || null },
    paddle: { priceId: priceBlock.paddle?.priceId || null },
    payPro: { priceId: priceBlock.payPro?.priceId ?? null }
  }
}

export const getMergedPlan = (planId, currencyOrSymbol) => {
  const plan = PLANS_ID[planId]
  if (!plan) return null

  return {
    ...plan,
    prices: {
      yearly: getPlanPrice(planId, 'yearly', currencyOrSymbol),
      monthly: getPlanPrice(planId, 'monthly', currencyOrSymbol)
    }
  }
}

export const getAllPlanPricesForCurrency = (interval, currencyOrSymbol) => {
  const code = normalizeCurrencyCode(currencyOrSymbol)
  const symbol = CURRENCY_CODE_TO_SYMBOL[code] || '$'
  return {
    freePlan: { symbol, value: 0 },
    tinyPlan: getPlanPrice(TINY_PLAN.id, interval, currencyOrSymbol),
    soloPlan: getPlanPrice(SOLO_PLAN.id, interval, currencyOrSymbol),
    proPlan: getPlanPrice(PRO_PLAN.id, interval, currencyOrSymbol),
    maxPlan: getPlanPrice(MAX_PLAN.id, interval, currencyOrSymbol),
    apiPlan: getPlanPrice(API_PLAN.id, interval, currencyOrSymbol)
  }
}

export const PLAN_THRESHOLDS = {
  FREE: {
    projectLimit: FREE_PLAN.projectLimit,
    fileSizeLimit: convertToBytes(FREE_PLAN.fileSizeLimit),
    visitorLimit: FREE_PLAN.visitorLimit,
    bandwidthLimit: FREE_PLAN.bandwidthLimit,
    customDomainsLimit: FREE_PLAN.customDomainsLimit
  },
  TINY: {
    projectLimit: TINY_PLAN.projectLimit,
    fileSizeLimit: convertToBytes(TINY_PLAN.fileSizeLimit),
    visitorLimit: TINY_PLAN.visitorLimit,
    bandwidthLimit: TINY_PLAN.bandwidthLimit,
    customDomainsLimit: TINY_PLAN.customDomainsLimit
  },
  SOLO: {
    projectLimit: SOLO_PLAN.projectLimit,
    fileSizeLimit: convertToBytes(SOLO_PLAN.fileSizeLimit),
    visitorLimit: SOLO_PLAN.visitorLimit,
    bandwidthLimit: SOLO_PLAN.bandwidthLimit,
    customDomainsLimit: SOLO_PLAN.customDomainsLimit
  },
  PRO: {
    projectLimit: PRO_PLAN.projectLimit,
    fileSizeLimit: convertToBytes(PRO_PLAN.fileSizeLimit),
    visitorLimit: PRO_PLAN.visitorLimit,
    bandwidthLimit: PRO_PLAN.bandwidthLimit,
    customDomainsLimit: PRO_PLAN.customDomainsLimit
  },
  API: {
    projectLimit: API_PLAN.projectLimit,
    fileSizeLimit: convertToBytes(API_PLAN.fileSizeLimit),
    visitorLimit: API_PLAN.visitorLimit,
    bandwidthLimit: API_PLAN.bandwidthLimit,
    customDomainsLimit: API_PLAN.customDomainsLimit
  },
  MAX: {
    projectLimit: MAX_PLAN.projectLimit,
    fileSizeLimit: convertToBytes(MAX_PLAN.fileSizeLimit),
    visitorLimit: MAX_PLAN.visitorLimit,
    bandwidthLimit: MAX_PLAN.bandwidthLimit,
    customDomainsLimit: MAX_PLAN.customDomainsLimit
  }
}

export const ALL_FEATURES = [
  {
    label: 'Remove Tiiny Host banner',
    tooltip: 'No Tiiny Host branding on your content',
    enabled: [PLANS.TINY.id, PLANS.PRO.id, PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Alternate domains',
    tooltip: 'Upload to our exclusive & more reliable .tiiny.co domain',
    enabled: [PLANS.TINY.id, PLANS.PRO.id, PLANS.PRO_U.id]
  },
  {
    label: 'QR Codes',
    tooltip: 'Autogenerated QR codes to download & share',
    enabled: [PLANS.TINY.id, PLANS.PRO.id, PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Custom domains',
    tooltip: 'Use your website name to publish your content, custom SSL included',
    enabled: [PLANS.PRO.id, PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Built-in analytics',
    tooltip: "Automatically track the number of visitors and where they're coming from",
    enabled: [PLANS.TINY.id, PLANS.PRO.id, PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Link Tracking',
    tooltip: 'Create unique links for a specific recipient to track when it’s opened',
    enabled: [PLANS.PRO.id, PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Editing features',
    tooltip: 'Easily edit websites, text in PDFs, HTML, CSS & JavaScript files online using our built-in editors',
    enabled: [PLANS.PRO.id, PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Password protection',
    tooltip: 'Restrict who can view your content by adding a password',
    enabled: [PLANS.PRO.id, PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Capture emails',
    tooltip: 'Capture potential leads by requiring visitors to enter their email address',
    enabled: [PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Disable PDF download/printing',
    tooltip: 'Prevent visitors from downloading or printing your PDF',
    enabled: [PLANS.PRO.id, PLANS.PRO_U.id]
  },
  {
    label: 'Custom 404 pages',
    tooltip: 'Configure your own 404 error pages when content not found',
    enabled: [PLANS.PRO.id, PLANS.PRO_U.id]
  },
  {
    label: 'Integrations',
    tooltip: 'e.g. Zapier',
    enabled: [PLANS.PRO.id, PLANS.PRO_U.id]
  },
  {
    label: 'Feedback mode',
    tooltip: 'Allow visitors to leave feedback through comments',
    enabled: [PLANS.TINY.id, PLANS.PRO.id, PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'API access',
    tooltip: 'Create & update projects via our API',
    enabled: [PLANS.PRO.id, PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Add team members',
    tooltip: 'Share account with up to 1 more user (upgradeable)',
    enabled: [PLANS.PRO_U.id],
    featured: true
  },
  {
    label: 'Priority support',
    tooltip: 'Book a call with our helpful support team',
    enabled: [PLANS.PRO_U.id],
    featured: true
  }
]

export const PRICING_FEATURES = {
  [PLANS.FREE.id]: [],
  [PLANS.TINY.id]: [
    {
      label: 'Remove Tiiny Host banner',
      tooltip: 'No Tiiny Host branding on your content',
      icon: 'swatch.svg',
      checkicon: 'check-pricing-tiny.svg'
    },
    {
      label: 'QR Codes',
      tooltip: 'Generate unique QR codes for your content',
      icon: 'qr-code-blue.svg',
      checkicon: 'check-pricing-tiny.svg'
    },
    {
      label: 'Built-in analytics',
      tooltip: 'Track engagement and performance',
      icon: 'chart-bar-blue.svg',
      checkicon: 'check-pricing-tiny.svg'
    },
    {
      label: 'Feedback mode',
      tooltip: 'Allow visitors to leave feedback through comments',
      icon: 'comment-outline-yellow.svg',
      checkicon: 'check-pricing-pro.svg'
    }
  ],
  [PLANS.PRO.id]: [
    {
      label: 'Everything in Tiny Plan',
      tooltip: 'All features of Tiny plan',
      icon: 'plus-circle-solo.svg',
      checkicon: 'check-pricing-solo.svg'
    },
    {
      label: 'Custom domains',
      tooltip: 'Use your own domain for hosting',
      icon: 'domain-solo.svg',
      checkicon: 'check-pricing-solo.svg'
    },
    {
      label: 'Editing features',
      tooltip: 'Edit content on the fly',
      icon: 'pencil-square.svg',
      checkicon: 'check-pricing-solo.svg'
    },
    {
      label: 'Password protection',
      tooltip: 'Secure your content with a password',
      icon: 'lock-closed.svg',
      checkicon: 'check-pricing-solo.svg'
    },
    {
      label: 'API',
      tooltip: 'Create and update projects via API',
      icon: 'power-purple.svg',
      checkicon: 'check-pricing-solo.svg'
    }
  ],
  [PLANS.PRO_U.id]: [
    {
      label: 'Everything in Solo Plan',
      tooltip: 'All features of solo plan',
      icon: 'plus-circle-pro.svg',
      checkicon: 'check-pricing-pro.svg'
    },
    {
      label: 'Capture emails',
      tooltip: 'Collect email addresses from visitors',
      icon: 'envelope-pro.svg',
      checkicon: 'check-pricing-pro.svg'
    },
    {
      label: 'Add team members',
      tooltip: 'Invite your team to collaborate',
      icon: 'users-pro.svg',
      checkicon: 'check-pricing-pro.svg'
    },
    {
      label: 'Priority support',
      tooltip: 'Book a call with our helpful support team',
      icon: 'headphones.svg',
      checkicon: 'check-pricing-pro.svg'
    }
  ],
  [PLANS.PRO_MAX.id]: [
    {
      label: 'Everything in Pro Plan',
      tooltip: 'All features of pro plan',
      icon: 'plus-circle-black.svg',
      checkicon: 'check-black.svg'
    },
    {
      label: 'Large file hosting',
      tooltip: 'e.g. videos, images, etc.',
      icon: 'upload.svg',
      checkicon: 'check-black.svg'
    }
  ]
}

export const ARCHIVE_LIMIT_FREE_PLAN = 3
